import React, { useState } from 'react'
//components
import { Button } from '../../../../components/ui/buttons/Button'
import Label from '../../../../components/ui/labels/Label'
import { Input } from '../../../../components/ui/inputs/Input'
import { TextInput } from '../../../../components/ui/inputs/TextInput'
import { InputContainer } from '../../../../components/ui/inputs/InputContainers'
//context
import { useError } from '@contexts/ErrorContext'
import { useSuccess } from '@contexts/SuccessContext'
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
import { useLoading } from '@contexts/LoadingContext'
//api
import { createPOIGroupAPI } from '@services/poiGroupsAPI'

/**
 * Component for PoiGroups tab to create a new POI Group - group name and description only
 * 
 * @param {Function} setGroupDetails to fill in PoiGroupDetails with new group data
 * @param {Function} handleChangeGroup to update group dropdown with new group
 * @param {Function} setPermissionSortedPoiGroups to update group dropdown with new group
 * @param {Function} setIsAddNewGroup to switch from this component to PoiGroupDetails on new group save
 * @param {Object} selectedJob job selected in parent, format: {label: "Job1", value: 1}
 * 
 * @returns {JSX.Element}
 */
export const AddNewGroup = ({setGroupDetails, handleChangeGroup, setPermissionSortedPoiGroups, setIsAddNewGroup, selectedJob}) => {
        const [groupName, setGroupName] = useState("")
        const [groupDescription, setGroupDescription] = useState("")
        const [validationErrors, setValidationErrors] = useState({})
    
        const {showError} = useError()
        const {timeoutSuccess} = useSuccess()
        const {setHasUnsavedChanges} = useUnsavedChanges()
        const {showLoading, hideLoading}  = useLoading()

        const handleAddGroup = async() => {
            showLoading("Adding POI group...")
            
            //validation
            const errors = {}

            if(!groupName){
                errors.groupName = "Group name is required"
            }

            if(!groupDescription){
                errors.groupDescription = "Description is required"
            }

            //if there are errors, set state to errors and return false
            if(Object.keys(errors).length > 0){
                setValidationErrors(errors)
                hideLoading()
                showError("Could not add group - some form fields are missing")
                return
            }

            setValidationErrors({})
    
            //call api
            const response = await createPOIGroupAPI({
                POIGroup_Name: groupName,
                Description: groupDescription,
                JobID: selectedJob.value
            })
                            
            if(!response.success){
                hideLoading()
    
                showError(`Error adding POI group: ${response.message}`)
    
                return
            }
    
            const newGroup = response.data
    
            const newGroupNameOption = {
                value: newGroup.POIGroupID, 
                label: newGroup.POIGroup_Name,
                IsEditor: true,
                IsUserInGroup: true
            }
            
            //change selected group to new group
            handleChangeGroup(newGroupNameOption)

            //add to nested dropdown options
            setPermissionSortedPoiGroups(prevData => prevData.map(group => {
                if (group.label === "Editor") {
                    return {
                        ...group,
                        options: [...group.options, newGroupNameOption]
                    };
                }
                return group;
            }));

            //fill in PoiGroupDetails component with new group data
            setGroupDetails(response.data)

            setIsAddNewGroup(false)
    
            setHasUnsavedChanges(false)
    
            hideLoading()
    
            timeoutSuccess("POI group saved")
        }
    return(
        <>
            {/* group name */}
            <InputContainer>
                <Label htmlFor='groupName' className='mb-2 font-semibold'>POI group name</Label>
                <Input 
                    name="groupName" 
                    value={groupName} 
                    onChange={(e)=>setGroupName(e.target.value)} 
                    error={validationErrors.groupName}
                />
            </InputContainer>

            {/* group description */}
            <InputContainer>
                <Label htmlFor='groupDescription' className='mb-2 font-semibold'>POI group description</Label>
                <TextInput 
                    name="groupDescription" 
                    value={groupDescription} 
                    onChange={(e)=>setGroupDescription(e.target.value)} 
                    error={validationErrors.groupDescription} 
                />
            </InputContainer>

            <div>
                <Button variant="success" onClick={() => handleAddGroup()} className='mr-2'> Add Group</Button>
                <Button variant="cancel" onClick={() => setIsAddNewGroup(false)}>Cancel</Button>
            </div>
        </>

    )
}